import React, { createContext, useContext, useEffect, useState } from 'react';
import { Business, UserProfile, UserBusinessInfo } from '../types/database';
import { businessService, userProfileService } from '../utils/supabase';
import { useAuth } from './AuthContext';

interface BusinessContextType {
  business: Business | null;
  userProfile: UserProfile | null;
  userBusinesses: UserBusinessInfo[];
  currentUserRole: string | null;
  loading: boolean;
  error: string | null;
  createBusiness: (name: string, industry?: string) => Promise<{ success: boolean; error?: string }>;
  updateBusiness: (updates: { name?: string; industry?: string }) => Promise<{ success: boolean; error?: string }>;
  refreshBusinessData: () => Promise<void>;
  switchBusiness: (businessId: string) => void;
  isAdmin: boolean;
  isManager: boolean;
  currentBusiness: Business | null;
}

const BusinessContext = createContext<BusinessContextType | undefined>(undefined);

export const useBusiness = () => {
  const context = useContext(BusinessContext);
  if (context === undefined) {
    throw new Error('useBusiness must be used within a BusinessProvider');
  }
  return context;
};

interface BusinessProviderProps {
  children: React.ReactNode;
}

export const BusinessProvider: React.FC<BusinessProviderProps> = ({ children }) => {
  const [business, setBusiness] = useState<Business | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [userBusinesses, setUserBusinesses] = useState<UserBusinessInfo[]>([]);
  const [currentUserRole, setCurrentUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const refreshBusinessData = async () => {
    if (!user) {
      setBusiness(null);
      setUserProfile(null);
      setUserBusinesses([]);
      setCurrentUserRole(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get user profile
      const { data: profileData, error: profileError } = await userProfileService.getCurrentProfile();
      
      if (profileError) {
        setError('Failed to load user profile');
        return;
      }

      setUserProfile(profileData);
      
      // Get user's businesses
      const { data: businessesData, error: businessesError } = await businessService.getUserBusinesses();
      
      if (businessesError) {
        setError('Failed to load businesses');
        return;
      }

      setUserBusinesses(businessesData || []);
      
      // Set the first business as active (or maintain current selection)
      if (businessesData && businessesData.length > 0) {
        const currentBusiness = business 
          ? businessesData.find(b => b.business_id === business.id)
          : businessesData[0];
        
        if (currentBusiness) {
          // Get full business details
          const { data: businessData, error: businessError } = await businessService.getBusinessById(currentBusiness.business_id);
          
          if (businessError) {
            setError('Failed to load business details');
            return;
          }
          
          setBusiness(businessData);
          setCurrentUserRole(currentBusiness.user_role);
        } else {
          setBusiness(null);
          setCurrentUserRole(null);
        }
      } else {
        setBusiness(null);
        setCurrentUserRole(null);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const switchBusiness = async (businessId: string) => {
    const targetBusiness = userBusinesses.find(b => b.business_id === businessId);
    if (!targetBusiness) return;

    try {
      setLoading(true);
      const { data: businessData, error: businessError } = await businessService.getBusinessById(businessId);
      
      if (businessError) {
        setError('Failed to switch business');
        return;
      }
      
      setBusiness(businessData);
      setCurrentUserRole(targetBusiness.user_role);
    } catch (err) {
      setError('Failed to switch business');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshBusinessData();
  }, [user]);

  const createBusiness = async (name: string, industry?: string) => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: createError } = await businessService.createBusiness(name, industry);
      
      if (createError) {
        setError(createError.message);
        return { success: false, error: createError.message };
      }

      // Refresh data to get the new business
      await refreshBusinessData();
      
      return { success: true };
    } catch (err) {
      const errorMessage = 'Failed to create business';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const updateBusiness = async (updates: { name?: string; industry?: string }) => {
    if (!business) {
      return { success: false, error: 'No business to update' };
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: updateError } = await businessService.updateBusiness(business.id, updates);
      
      if (updateError) {
        setError(updateError.message);
        return { success: false, error: updateError.message };
      }

      if (data) {
        setBusiness(data);
      }
      
      return { success: true };
    } catch (err) {
      const errorMessage = 'Failed to update business';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Legacy role checks for backward compatibility
  const isAdmin = currentUserRole === 'admin';
  const isManager = currentUserRole === 'manager' || isAdmin;

  const value = {
    business,
    userProfile,
    userBusinesses,
    currentUserRole,
    loading,
    error,
    createBusiness,
    updateBusiness,
    refreshBusinessData,
    switchBusiness,
    isAdmin,
    isManager,
    currentBusiness: business // Add this alias for clarity
  };

  return (
    <BusinessContext.Provider value={value}>
      {children}
    </BusinessContext.Provider>
  );
};